import { useMemo, type ReactNode } from 'react';
import styled from 'styled-components';
import type { FleetModelPrc } from '@shared/api';
import { formatIsoDate, periodText, prcTimeline, rateUnit } from '../groups';
import { formatDateTime, formatMoney, typo } from '../model';
import { Notice } from './kit';
import { Matrix, MatrixBox } from './passportParts';
import { ScrollRegion } from './ScrollRegion';
import { fleetLeading, fleetType } from './tokens';

const Section = styled.section`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};

  & > h3 {
    margin: 0;
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & > .unit {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Три цифры цены — как графы бланка: без НДС крупно, рядом с НДС и себестоимость. */
const Figures = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) repeat(2, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;

  & > div {
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  & dt {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & dd.big {
    font-size: ${fleetType.title};
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  @container drawer (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > div:first-child {
      grid-column: 1 / -1;
    }
  }
`;

const Meta = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Empty = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > p {
    margin: 0;
    text-wrap: pretty;
  }

  & > p + p,
  & > p + div {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }

  & strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Stack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const HistoryTable = styled(Matrix)`
  thead th.version {
    text-align: left;
  }

  td.version {
    text-align: left;
    white-space: normal;
    min-width: 160px;
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  td.now {
    font-weight: 600;
  }
`;

const Mark = styled.span`
  display: block;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function approvedText(p: FleetModelPrc): string {
  const who = p.approved_by_name ? `${p.approved_by_name}, ` : '';
  return `${who}${formatDateTime(p.approved_at)}`;
}

function versionText(p: FleetModelPrc): string {
  const label = p.version_label ? `«${p.version_label}»` : 'версия без названия';
  const version = p.variant_name ? `${label}, вариант «${p.variant_name}»` : label;
  return p.project_name ? `${version}, объект «${p.project_name}»` : version;
}

/**
 * Цены модели для объекта (миграция 0333): своя история объекта, а если её
 * нет — общие цены. Без объекта — общие; у модели без общих — все цены, с
 * подписью объекта у каждой.
 */
function historyForObject(
  history: readonly FleetModelPrc[],
  projectId: string | undefined,
): readonly FleetModelPrc[] {
  const own = projectId ? history.filter((p) => p.project_id === projectId) : [];
  if (own.length > 0) return own;
  const common = history.filter((p) => !p.project_id);
  return common.length > 0 || projectId ? common : history;
}

function PriceFigures({ prc }: { prc: FleetModelPrc }) {
  return (
    <Figures aria-label="Цена машино-часа">
      <div>
        <dt>Без НДС</dt>
        <dd className="big">{formatMoney(prc.price)}</dd>
      </div>
      <div>
        <dt>С НДС</dt>
        <dd>{formatMoney(prc.price_with_vat)}</dd>
      </div>
      <div>
        <dt>Себестоимость</dt>
        <dd>{formatMoney(prc.cost)}</dd>
      </div>
    </Figures>
  );
}

interface ModelPrcBlockProps {
  /** Утверждённые ПРЦ модели (`prc_history` карточки). */
  history: readonly FleetModelPrc[];
  /** Сегодня, `YYYY-MM-DD`: по нему — какая цена действует. */
  today: string;
  idPrefix: string;
  /** Что предложить, когда цены нет: ссылка на версии ПРЦ. */
  emptyAction?: ReactNode;
  /** Объект из шапки ОСНОВЫ: его цена (своя или общая). */
  projectId?: string;
}

/**
 * «Утверждённая ПРЦ» в карточке модели: действующая цена маш.-часа без и с
 * НДС, себестоимость, базовый год, «действует с», версия и кто утвердил;
 * цена, которая ещё не вступила в силу; история прежних цен с датами.
 */
export function ModelPrcBlock({
  history: all,
  today,
  idPrefix,
  emptyAction,
  projectId,
}: ModelPrcBlockProps) {
  const history = useMemo(() => historyForObject(all, projectId), [all, projectId]);
  const t = useMemo(() => prcTimeline(history, today), [history, today]);
  const headingId = `${idPrefix}-prc-h`;
  const shown = t.current ?? t.upcoming[0] ?? null;
  const next = t.current ? t.upcoming[0] : t.upcoming[1];
  const rows = useMemo(
    () => [...history].sort((a, b) => b.valid_from.localeCompare(a.valid_from)),
    [history],
  );

  return (
    <Section aria-labelledby={headingId}>
      <Head>
        <h3 id={headingId}>Утверждённая ПРЦ</h3>
        {shown && (
          <span className="unit">
            {rateUnit('machine_hour')}, базовый год {shown.base_year}
          </span>
        )}
      </Head>

      {!shown ? (
        <Empty>
          <p>
            <strong>ПРЦ не утверждена.</strong>{' '}
            {typo(
              'Цена машино-часа появится здесь, когда экономист утвердит версию ПРЦ с этой моделью. До тех пор модель не входит в подсказку средней ставки группы.',
            )}
          </p>
          {emptyAction && <div>{emptyAction}</div>}
        </Empty>
      ) : (
        <>
          <PriceFigures prc={shown} />
          <Meta>
            {t.current ? (
              <>
                Действует <strong>{periodText(shown.valid_from, shown.valid_to)}</strong>
              </>
            ) : (
              <>
                Вступит в силу <strong>{formatIsoDate(shown.valid_from)}</strong> — сегодня
                утверждённой цены нет
              </>
            )}
            {` · версия ${versionText(shown)} · утверждено: ${approvedText(shown)}`}
          </Meta>
        </>
      )}

      {next && (
        <Stack>
          <Notice $tone="info" role="note">
            <span className="text">
              {typo(
                `С ${formatIsoDate(next.valid_from)} — новая цена ${formatMoney(next.price)} ₽ без НДС (${formatMoney(next.price_with_vat)} ₽ с НДС), версия ${versionText(next)}.`,
              )}
            </span>
          </Notice>
        </Stack>
      )}

      {rows.length > 1 && (
        <MatrixBox>
          <ScrollRegion label="История цен машино-часа">
            <HistoryTable>
              <caption>История цен, ₽ за маш.-час</caption>
              <thead>
                <tr>
                  <th scope="col">Период</th>
                  <th scope="col">Без НДС</th>
                  <th scope="col">С НДС</th>
                  <th scope="col">Себестоимость</th>
                  <th scope="col" className="version">
                    Версия, кем утверждена
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const now = p.id === t.current?.id;
                  const future = p.valid_from > today;
                  return (
                    <tr key={p.id}>
                      <th scope="row">
                        {(now || future) && <Mark>{now ? 'действует' : 'вступит в силу'}</Mark>}
                        {periodText(p.valid_from, p.valid_to)}
                      </th>
                      <td className={now ? 'now' : undefined}>{formatMoney(p.price)}</td>
                      <td>{formatMoney(p.price_with_vat)}</td>
                      <td>{formatMoney(p.cost)}</td>
                      <td className="version">
                        {versionText(p)}, {approvedText(p)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </HistoryTable>
          </ScrollRegion>
        </MatrixBox>
      )}
    </Section>
  );
}
